import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { PA_OPTIONS, PA_DETAILS } from "../constants/directoryOptions";

/**
 * Practice-area picker for the consumer directory.
 *
 * A plain <select> can only show the category name, which assumes the visitor
 * already knows which legal label covers their problem. Each row here carries a
 * +/- expander that reveals the kinds of matters that fall under it, so someone
 * who only knows "my landlord is evicting me" can find the right filter.
 *
 * Expansion is deliberately independent of selection: opening a description
 * must not change the active filter, and vice versa.
 */
export default function PracticeAreaSelect({ value, onChange, id = "fa-area" }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState([]);
  const wrapRef = useRef(null);

  // Close on outside click / Escape so the panel never strands the page.
  useEffect(() => {
    if (!open) return undefined;

    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggleExpanded = (name) =>
    setExpanded((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );

  const select = (name) => {
    onChange(name);
    setOpen(false);
  };

  return (
    <div className="pa-select" ref={wrapRef}>
      <button
        type="button"
        id={id}
        className={`pa-select-toggle${open ? " open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{value || "All practice areas"}</span>
        <ChevronDown
          size={16}
          className={`pa-select-chevron${open ? " open" : ""}`}
        />
      </button>

      {open && (
        <div className="pa-select-panel" role="listbox">
          <div
            className={`pa-select-all${value === "" ? " selected" : ""}`}
            role="option"
            aria-selected={value === ""}
            tabIndex={0}
            onClick={() => select("")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                select("");
              }
            }}
          >
            <span>All practice areas</span>
            {value === "" && <Check size={16} className="pa-select-check" />}
          </div>

          {PA_OPTIONS.map((name) => {
            const isSelected = value === name;
            const isExpanded = expanded.includes(name);

            return (
              <div className="pa-select-item" key={name}>
                <div className={`pa-select-row${isSelected ? " selected" : ""}`}>
                  <button
                    type="button"
                    className="pa-select-expander"
                    aria-label={`${isExpanded ? "Hide" : "Show"} what's included in ${name}`}
                    aria-expanded={isExpanded}
                    onClick={(e) => {
                      // Expanding must not also pick the filter.
                      e.stopPropagation();
                      toggleExpanded(name);
                    }}
                  >
                    {isExpanded ? "−" : "+"}
                  </button>
                  <div
                    className="pa-select-label"
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    onClick={() => select(name)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        select(name);
                      }
                    }}
                  >
                    <span>{name}</span>
                    {isSelected && (
                      <Check size={16} className="pa-select-check" />
                    )}
                  </div>
                </div>

                {isExpanded && PA_DETAILS[name] && (
                  <p className="pa-select-detail">{PA_DETAILS[name]}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
